/* global BACKEND_DOMAIN */

// Bootstrap redirect (Plan 003 §7 Phase 1 — first-boot install wizard).
// Shared between `services/installerGuard.js` (runs from the router,
// before any component/pinia context exists) and
// `components/Installer/InstallWizard.vue` (runs inside a mounted
// component) — both need the exact same "is this instance installed?"
// answer and the exact same localStorage cache key, so that logic lives
// here once instead of twice.

const INSTALLED_CACHE_KEY = 'goshawk.installer.installed';

// Pre-rename key (Plan 006 — Platform Rename). Read-only fallback so a
// browser that already completed the on-prem wizard under the old
// "daedalus" key isn't shown the install wizard again once. Never written
// to — new installs only ever set/read the current key above.
const LEGACY_INSTALLED_CACHE_KEY = 'daedalus.installer.installed';

function isCachedInstalled() {
  try {
    if (window.localStorage.getItem(INSTALLED_CACHE_KEY) === '1') {
      return true;
    }
    return window.localStorage.getItem(LEGACY_INSTALLED_CACHE_KEY) === '1';
  } catch {
    // Storage unavailable (private browsing, quota, disabled) — just means
    // every navigation re-checks; never a hard failure.
    return false;
  }
}

function markInstalled() {
  try {
    window.localStorage.setItem(INSTALLED_CACHE_KEY, '1');
  } catch {
    // See isCachedInstalled() — harmless to skip.
  }
}

// A bare `fetch()` call, deliberately NOT icarus-core's `useApi()` (axios):
// `installerGuard.js` calls this from `router.beforeEach`, which runs
// before `app.use(pinia)`/`app.use(router)` have necessarily settled a
// component context `useApi()`'s composables (useRouter/useTokenStore)
// need — and axios itself isn't a direct icarus dependency (it's private
// to `@apexl/icarus-core`'s own `node_modules`, unreachable from project
// code without adding a redundant top-level dependency). `GET
// /api/v1/installer/status` is unauthenticated by design anyway (see
// `api/module/installer/routes/api/installer.php`), so none of
// `useApi()`'s auth-header/refresh machinery is needed here — `fetch` is
// sufficient.
//
// Timeout: a bare `fetch()` has no default timeout, and this call sits
// directly in `router.beforeEach` — if it hangs (dropped connection, a
// proxy that swallows the request, backend momentarily unreachable), the
// guard can never resolve and every navigation in the SPA stalls forever,
// which is worse than the network hiccup itself. `installInstallerGuard`'s
// own catch block already "fails open" on a *rejected* status check; the
// timeout below is what guarantees a hang eventually turns into a
// rejection instead of sitting pending indefinitely.
const STATUS_FETCH_TIMEOUT_MS = 4000;

/**
 * Resolves the installer's current status against the live API.
 *
 * Returns `{ installed: true, state: null }` once the wizard is complete —
 * the API 404s at that point by design (`InstallerController`'s
 * inert-when-complete short-circuit) — or `{ installed: false, state }`
 * for any `needs-*` state. Network/server errors (including a timeout) are
 * rethrown; callers decide how to fail (the guard fails open, the wizard
 * shows an error).
 */
async function fetchInstallerStatus() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), STATUS_FETCH_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${BACKEND_DOMAIN}/api/v1/installer/status`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(`installer/status timed out after ${STATUS_FETCH_TIMEOUT_MS}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 404) {
    return { installed: true, state: null };
  }

  if (!response.ok) {
    throw new Error(`Unexpected installer/status response: ${response.status}`);
  }

  const data = await response.json();

  return { installed: false, state: data.state };
}

export {
  INSTALLED_CACHE_KEY,
  isCachedInstalled,
  markInstalled,
  fetchInstallerStatus,
};
